import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PostCard from "@/components/PostCard";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import HomeHero from "@/components/HomeHero";
import TrendingStrip from "@/components/TrendingStrip";
import LatestNewsCarousel from "@/components/LatestNewsCarousel";
import InstagramFeed from "@/components/InstagramFeed";
import { applySeo, SITE_URL } from "@/lib/seo";
import { ArrowRight, Lightning } from "@phosphor-icons/react";

const categoryStrip = ["BREAKING", "REVIEWS", "DEEP DIVES", "VIDEOS", "AI", "GADGETS", "OPINION", "BUILDS"];

const HOME_SEO = {
    title: "Tizona Tech — Tech News, Reviews & Videos",
    description: "Sharp takes on the gear, code, and ideas shaping tomorrow. Latest tech news, hands-on reviews, and videos from Tizona Tech.",
    path: "/",
    type: "website",
    jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Tizona Tech",
        "url": SITE_URL,
        "potentialAction": {
            "@type": "SearchAction",
            "target": `${SITE_URL}/category/news?q={search_term_string}`,
            "query-input": "required name=search_term_string",
        },
    },
};

export default function Home() {
    const [news, setNews] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [features, setFeatures] = useState([]);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        applySeo(HOME_SEO);
        (async () => {
            try {
                // Fetch per category so no single category can starve the others
                // (previously we hit /api/posts once with a 50-item cap, and 44 news
                // posts pushed videos & extra reviews off the list on production).
                const [n, r, f, v] = await Promise.all([
                    api.get("/posts?category=news&limit=12"),
                    api.get("/posts?category=reviews&limit=6"),
                    api.get("/posts?category=features&limit=6"),
                    api.get("/posts?category=videos&limit=6"),
                ]);
                setNews(n.data || []);
                setReviews((r.data || []).slice(0, 3));
                setFeatures((f.data || []).slice(0, 3));
                setVideos((v.data || []).slice(0, 3));
            } catch {
                setNews([]); setReviews([]); setFeatures([]); setVideos([]);
            } finally { setLoading(false); }
        })();
    }, []);

    return (
        <div data-testid="home-page">
            {/* Marquee */}
            <div className="border-b border-zinc-800 overflow-hidden bg-[#FFD000]" data-testid="marquee">
                <div className="marquee-track py-2 font-mono text-[11px] font-bold tracking-[0.25em] text-black uppercase">
                    {[...categoryStrip, ...categoryStrip, ...categoryStrip].map((s, i) => (
                        <span key={`${s}-${i}`} className="px-6 flex items-center gap-6">
                            {s} <Lightning size={12} weight="fill" />
                        </span>
                    ))}
                </div>
            </div>

            <HomeHero posts={news} />

            {news.length > 0 && <TrendingStrip posts={news.slice(0, 5)} />}

            {loading ? (
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
                    Loading the feed...
                </div>
            ) : (
                <>
                    {/* News */}
                    <SectionBlock title="Latest News" linkTo="/category/news" testId="section-news">
                        <LatestNewsCarousel posts={news} />
                    </SectionBlock>

                    {/* Reviews */}
                    <SectionBlock title="Reviews" linkTo="/category/reviews" testId="section-reviews">
                        <Stagger className="grid md:grid-cols-3 gap-px bg-zinc-800" stagger={0.12}>
                            {reviews.map(p => (
                                <StaggerItem key={p.id} className="bg-[#0A0A0A]"><PostCard post={p} /></StaggerItem>
                            ))}
                        </Stagger>
                    </SectionBlock>

                    {/* Features */}
                    {features.length > 0 && (
                        <SectionBlock title="Features" linkTo="/category/features" testId="section-features">
                            <Stagger className="grid md:grid-cols-3 gap-px bg-zinc-800" stagger={0.12}>
                                {features.map(p => (
                                    <StaggerItem key={p.id} className="bg-[#0A0A0A]"><PostCard post={p} /></StaggerItem>
                                ))}
                            </Stagger>
                        </SectionBlock>
                    )}

                    {/* Videos */}
                    <SectionBlock title="Videos" linkTo="/category/videos" testId="section-videos">
                        <Stagger className="grid md:grid-cols-3 gap-px bg-zinc-800" stagger={0.12}>
                            {videos.map(p => (
                                <StaggerItem key={p.id} className="bg-[#0A0A0A]"><PostCard post={p} /></StaggerItem>
                            ))}
                        </Stagger>
                    </SectionBlock>

                    {/* Instagram feed below videos */}
                    <InstagramFeed />

                    <SocialCta />
                </>
            )}
        </div>
    );
}

function SectionBlock({ title, linkTo, children, testId }) {
    return (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24" data-testid={testId}>
            <Reveal className="flex items-end justify-between border-b border-zinc-800 pb-6 mb-10">
                <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight leading-none">
                    {title}<span className="text-[#FFD000]">.</span>
                </h2>
                <Link to={linkTo} className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#FFD000] inline-flex items-center gap-2 link-slide">
                    View All <ArrowRight size={14} weight="bold" />
                </Link>
            </Reveal>
            {children}
        </section>
    );
}

function SocialCta() {
    return (
        <section className="border-t border-zinc-800 mt-24" data-testid="social-cta">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-12 items-center">
                <Reveal>
                    <div className="overline mb-4">// Stay Connected</div>
                    <h2 className="font-display text-5xl md:text-6xl text-white leading-[0.9] tracking-tight">
                        SUBSCRIBE.<br />
                        <span className="text-[#FFD000]">FOLLOW.</span><br />
                        NEVER MISS.
                    </h2>
                </Reveal>
                <Stagger className="grid grid-cols-1 gap-px bg-zinc-800" stagger={0.15}>
                    <StaggerItem>
                        <a href="https://www.youtube.com/channel/UCK7IwwD0gvvompUwdg09GmA" target="_blank" rel="noreferrer" data-testid="social-cta-youtube"
                           className="bg-[#0A0A0A] p-8 hover:bg-zinc-900 transition-colors group flex items-center justify-between">
                            <div>
                                <div className="overline mb-2">YouTube</div>
                                <div className="font-display text-3xl text-white">@tizonatech</div>
                            </div>
                            <ArrowRight size={32} className="text-zinc-500 group-hover:text-[#FFD000] group-hover:translate-x-2 transition-all" />
                        </a>
                    </StaggerItem>
                    <StaggerItem>
                        <a href="https://www.instagram.com/tizonatech" target="_blank" rel="noreferrer" data-testid="social-cta-instagram"
                           className="bg-[#0A0A0A] p-8 hover:bg-zinc-900 transition-colors group flex items-center justify-between">
                            <div>
                                <div className="overline mb-2">Instagram</div>
                                <div className="font-display text-3xl text-white">@tizonatech</div>
                            </div>
                            <ArrowRight size={32} className="text-zinc-500 group-hover:text-[#FFD000] group-hover:translate-x-2 transition-all" />
                        </a>
                    </StaggerItem>
                </Stagger>
            </div>
        </section>
    );
}
